import { useEffect, useState } from 'react';
import type { XeroStatus } from '@cca/xero-shared';
import {
  PortalHeader,
  SignOutMenu,
  GearIcon,
  resolveViewerTier,
  useSettingsDrawer,
  type ViewerTier,
} from '@cca/ui';
import { api } from './api/client';
import { FinanceSettings } from './pages/FinanceSettings';
import { FinanceSignIn } from './pages/FinanceSignIn';
import { InboxPage } from './pages/InboxPage';
import { BudgetPage } from './pages/BudgetPage';
import { ReceiptsPage } from './pages/ReceiptsPage';
import { ReimbursePage } from './pages/ReimbursePage';
import { ClaimFormPage } from './pages/ClaimFormPage';
import { QuotaPage } from './pages/QuotaPage';

type Page = 'inbox' | 'budget' | 'receipts' | 'reimburse' | 'quota';

const NAV: { id: Page; label: string }[] = [
  { id: 'inbox', label: 'Xero Inbox' },
  { id: 'budget', label: 'Budget' },
  { id: 'receipts', label: 'Donation Receipts' },
  { id: 'reimburse', label: 'Reimbursements' },
  { id: 'quota', label: 'Quota' },
];

export function App() {
  // Public claim form — no auth, separate top-level component
  const path = window.location.pathname;
  if (path === '/claim' || path === '/claim/') return <ClaimFormPage />;
  return <AuthenticatedApp />;
}

function AuthenticatedApp() {
  const [authed, setAuthed] = useState(() => !!localStorage.getItem('xero_session_token'));
  const [page, setPage] = useState<Page>('inbox');
  const [xeroStatus, setXeroStatus] = useState<XeroStatus | null>(null);
  const [xeroError, setXeroError] = useState<string | null>(null);
  const [viewer, setViewer] = useState<ViewerTier>('user');
  const settings = useSettingsDrawer();

  useEffect(() => {
    const onExpired = () => setAuthed(false);
    window.addEventListener('xero:session-expired', onExpired);
    return () => window.removeEventListener('xero:session-expired', onExpired);
  }, []);

  useEffect(() => {
    if (!authed) return;
    void api
      .authMe()
      .then((me) => setViewer(resolveViewerTier({ isRegistrar: me.isRegistrar, role: me.role })))
      .catch(() => null);
    void api
      .xeroStatus()
      .then(setXeroStatus)
      .catch(() => null);

    const params = new URLSearchParams(window.location.search);
    if (params.get('xero') === 'connected') {
      window.history.replaceState({}, '', window.location.pathname);
      void api
        .xeroStatus()
        .then(setXeroStatus)
        .catch(() => null);
    }
    if (params.get('xero') === 'error') {
      const reason = params.get('reason') ?? 'unknown';
      setXeroError(`Xero connection failed: ${reason}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [authed]);

  if (!authed) {
    return <FinanceSignIn onAuthenticated={() => setAuthed(true)} />;
  }

  // "Sign out all devices" lives here by the Sign out pill (account plumbing,
  // not work); the confirm dialog carries the explanation.
  const signOutAll = async () => {
    const message =
      'Sign out all devices?\n\nThis ends every signed-in Finance Portal session for your account, on every device, including this one. Other devices are signed out within about a minute. You can sign straight back in.';
    if (!window.confirm(message)) return;
    try {
      await api.authLogoutAll();
      localStorage.removeItem('xero_session_token');
      localStorage.removeItem('xero_session_role');
      setAuthed(false);
    } catch {
      window.alert('Could not sign out all devices. Please try again.');
    }
  };

  const signOutThisDevice = () =>
    void api
      .authLogout()
      .catch(() => null)
      .finally(() => {
        localStorage.removeItem('xero_session_token');
        localStorage.removeItem('xero_session_role');
        setAuthed(false);
      });

  // A fragment: PortalHeader owns the slot layout and its narrow-screen wrap (AUTH-1).
  const rightSlot = (
    <>
      <button
        type="button"
        className="btn-header"
        aria-label="Settings"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
        {...settings.buttonProps}
      >
        <GearIcon />
        Settings
      </button>
      <SignOutMenu onSignOut={signOutThisDevice} onSignOutAll={signOutAll} />
    </>
  );

  const tabNav = (
    <div className="tab-bar">
      <div
        style={{
          display: 'flex',
          maxWidth: 1100,
          margin: '0 auto',
          paddingLeft: 28,
          paddingRight: 28,
        }}
      >
        {NAV.map((n) => (
          <button
            key={n.id}
            className={`tab${page === n.id ? ' active' : ''}`}
            onClick={() => setPage(n.id)}
          >
            {n.label}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <>
      <PortalHeader portalName="Finance Portal" rightSlot={rightSlot} subNav={tabNav} />
      <FinanceSettings
        open={settings.open}
        onClose={settings.onClose}
        viewer={viewer}
        xeroStatus={xeroStatus}
        xeroError={xeroError}
        onXeroChanged={() =>
          void api
            .xeroStatus()
            .then(setXeroStatus)
            .catch(() => null)
        }
        onSignOut={signOutThisDevice}
        onSignOutAll={signOutAll}
      />
      <main>
        {page === 'inbox' && <InboxPage />}
        {page === 'budget' && <BudgetPage />}
        {page === 'receipts' && <ReceiptsPage />}
        {page === 'reimburse' && <ReimbursePage />}
        {page === 'quota' && <QuotaPage />}
      </main>
    </>
  );
}
